import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, ShieldCheck, Truck, Clock } from "lucide-react";
import { BUSINESS, IMG, SERVICES } from "../data/site";
import { CTAButtons, CTASection } from "../components/Sections";
import { useLanguage, usePath } from "../i18n/LanguageContext";

const STAT_ICONS = [ShieldCheck, Truck, Clock];

const Home = () => {
  const { t } = useLanguage();
  const p = usePath();
  const h = t.home;

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950" data-testid="hero-section">
        <img src={IMG.hero} alt="Stacked wood pallets in an industrial warehouse" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 lg:py-36">
          <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-amber-500 font-semibold mb-5 flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" /> {h.heroOverline}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl" data-testid="hero-headline">
            {h.heroTitle}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed" data-testid="hero-subheadline">
            {h.heroSub}
          </p>
          <div className="mt-9">
            <CTAButtons dark testidPrefix="hero" />
          </div>
          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4">
            {h.stats.map((label, i) => {
              const Icon = STAT_ICONS[i];
              return (
                <div key={label} className="flex items-center gap-2.5 text-slate-200 text-sm font-semibold" data-testid={`hero-stat-${i}`}>
                  <Icon className="h-5 w-5 text-amber-500" /> {label}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-[#FDFBF7] grain relative" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24">
          <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold mb-3">{h.servicesOverline}</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-xl leading-tight" data-testid="services-overview-title">
              {h.servicesTitle}
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-md leading-relaxed">{h.servicesText}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => {
              const tr = t.services[s.slug];
              return (
                <div
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="group bg-white border border-slate-200 p-7 hover:border-amber-600 hover:-translate-y-1 transition-[border-color,transform,box-shadow] duration-200 hover:shadow-lg hover:shadow-slate-900/5"
                >
                  <div className="h-11 w-11 bg-slate-950 flex items-center justify-center mb-5 group-hover:bg-amber-600 transition-colors">
                    <s.icon className="h-5 w-5 text-amber-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold tracking-tight text-slate-900 mb-2">{tr.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{tr.short}</p>
                  <Link
                    to={p(`/contact?service=${s.slug}`)}
                    data-testid={`service-card-${s.slug}-quote-link`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-700 hover:text-amber-800 transition-colors"
                  >
                    {h.requestQuote} <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-slate-950 text-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={IMG.aerialYard} alt="Pallet yard with stacked wood pallets" className="w-full h-72 lg:h-[460px] object-cover border border-slate-800" loading="lazy" />
            <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-amber-600 text-white px-6 py-4">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] font-semibold">{h.whyBadgeTop}</p>
              <p className="font-heading font-bold text-lg leading-tight mt-0.5">Inland Empire, CA</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-amber-500 font-semibold mb-3">{h.whyOverline}</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight" data-testid="why-choose-title">
              {h.whyTitle}
            </h2>
            <ul className="mt-8 space-y-4">
              {h.whyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-200" data-testid={`why-point-${i}`}>
                  <CheckCircle2 className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base font-medium">{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <CTAButtons dark testidPrefix="why" />
            </div>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="bg-white" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold mb-3">{h.areaOverline}</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="service-area-title">
              {h.areaTitle}
            </h2>
            <p className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed max-w-lg">{h.areaText}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {BUSINESS.cities.map((city) => (
                <span
                  key={city}
                  data-testid={`city-chip-${city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-200 bg-[#F8F6F0] text-sm font-semibold text-slate-800"
                >
                  <MapPin className="h-3.5 w-3.5 text-amber-600" /> {city}
                </span>
              ))}
              <span className="inline-flex items-center px-4 py-2 text-sm font-semibold text-amber-700">{h.areaMore}</span>
            </div>
          </div>
          <div className="relative">
            <img src={IMG.truck} alt="Delivery truck on the road" className="w-full h-72 lg:h-[400px] object-cover border border-slate-200" loading="lazy" />
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default Home;
